import { useParams } from '@tanstack/react-router';
import { Save, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { Loading } from '@/components/shared';
import { Button } from '@/components/ui/NewButton';
import { type CanonicalEntity, useEntities } from '@/hooks/useEntities';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import { roleLabel, sideLabel } from '@/lib/role-labels';

const SIDE_OPTIONS = ['claimant_side', 'respondent_side', 'neutral', 'unknown'] as const;

/** Mirrors the backend CaseRole enum. */
const ROLE_OPTIONS = [
  'party',
  'amicus',
  'legal_representative',
  'witness',
  'expert',
  'adjudicator',
  'court',
  'tribunal',
  'other',
] as const;

/** Roles where taking a side is meaningful; mirrors SIDEABLE_ROLE_TYPES on the backend. */
const SIDEABLE_ROLE_TYPES: readonly string[] = ['party', 'witness', 'legal_representative'];

interface Draft {
  name: string;
  description: string;
  aliasesText: string;
  /** role_type -> side, for sideable roles only. */
  roleSides: Record<string, string>;
  removedRoleTypes: string[];
  addedRoleTypes: string[];
}

function draftFor(entity: CanonicalEntity): Draft {
  const roleSides: Record<string, string> = {};
  for (const role of entity.roles) {
    if (SIDEABLE_ROLE_TYPES.includes(role.role_type)) roleSides[role.role_type] = role.side_alignment;
  }
  return {
    name: entity.canonical_name,
    description: entity.description ?? '',
    aliasesText: entity.stored_aliases.join(', '),
    roleSides,
    removedRoleTypes: [],
    addedRoleTypes: [],
  };
}

function parseAliases(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map(alias => alias.trim())
    .filter(Boolean);
}

function sameAliases(a: string[], b: string[]): boolean {
  const left = [...a].sort();
  const right = [...b].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function EntityEditor({
  entity,
  matterGuid,
  onSaved,
  onDeleted,
}: {
  entity: CanonicalEntity;
  matterGuid: string;
  onSaved: () => Promise<unknown>;
  onDeleted: () => Promise<unknown>;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFor(entity));
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const aliases = parseAliases(draft.aliasesText);
  const remainingRoles = entity.roles.filter(role => !draft.removedRoleTypes.includes(role.role_type));
  const addableRoles = ROLE_OPTIONS.filter(role =>
    !draft.addedRoleTypes.includes(role) && !remainingRoles.some(existing => existing.role_type === role)
  );

  // Sides for roles that survive this save, where the value differs from what's stored.
  const roleSides: Record<string, string> = {};
  for (const [roleType, side] of Object.entries(draft.roleSides)) {
    const kept = remainingRoles.some(role => role.role_type === roleType);
    const added = draft.addedRoleTypes.includes(roleType);
    if (!kept && !added) continue;
    const stored = entity.roles.find(role => role.role_type === roleType)?.side_alignment;
    if (side !== (stored ?? 'unknown')) roleSides[roleType] = side;
  }

  const payload: Record<string, unknown> = {};
  if (draft.name.trim() && draft.name.trim() !== entity.canonical_name) payload.name = draft.name.trim();
  if (draft.description !== (entity.description ?? '')) payload.description = draft.description;
  if (!sameAliases(aliases, entity.stored_aliases)) payload.aliases = aliases;
  if (draft.removedRoleTypes.length > 0) payload.remove_role_types = draft.removedRoleTypes;
  if (draft.addedRoleTypes.length > 0) payload.add_role_types = draft.addedRoleTypes;
  if (Object.keys(roleSides).length > 0) payload.role_sides = roleSides;
  const isDirty = Object.keys(payload).length > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      await backendClient.patch(`/entities/${matterGuid}/${entity.id}/admin`, payload);
      await onSaved();
      toast.success(`Updated ${entity.name}`);
      setDraft(current => ({ ...current, removedRoleTypes: [], addedRoleTypes: [] }));
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Error updating entity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await backendClient.delete(`/entities/${matterGuid}/${entity.id}`);
      await onDeleted();
      toast.success(`Deleted ${entity.name}`);
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err) || 'Error deleting entity');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-4 border-t border-gray-100 bg-gray-50 px-4 py-4">
      <div>
        <label
          htmlFor={`name-${entity.id}`}
          className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        >
          Canonical name
        </label>
        <input
          id={`name-${entity.id}`}
          value={draft.name}
          onChange={event => setDraft(current => ({ ...current, name: event.target.value }))}
          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
        />
        {entity.name !== entity.canonical_name && (
          <p className="mt-1 text-[11px] text-gray-400">
            Lists show &quot;{entity.name}&quot; instead, because display names use the most
            frequently observed name.
          </p>
        )}
      </div>

      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">Roles</p>
        {entity.roles.length === 0 && draft.addedRoleTypes.length === 0 ? (
          <p className="text-xs text-gray-400">No roles.</p>
        ) : (
          <div className="space-y-1.5">
            {[
              ...entity.roles.map(role => ({ roleType: role.role_type, pending: false })),
              ...draft.addedRoleTypes.map(roleType => ({ roleType, pending: true })),
            ].map(({ roleType, pending }) => {
              const removed = draft.removedRoleTypes.includes(roleType);
              const sideable = SIDEABLE_ROLE_TYPES.includes(roleType);
              return (
                <div key={roleType} className="flex items-center gap-2">
                  <span
                    className={`w-40 shrink-0 text-xs ${
                      removed ? 'text-red-500 line-through' : pending ? 'text-green-700' : 'text-gray-700'
                    }`}
                  >
                    {pending ? '+ ' : ''}{roleLabel(roleType)}
                  </span>
                  {sideable ? (
                    <select
                      aria-label={`Side for ${roleLabel(roleType)}`}
                      value={draft.roleSides[roleType] ?? 'unknown'}
                      disabled={removed}
                      onChange={event =>
                        setDraft(current => ({
                          ...current,
                          roleSides: { ...current.roleSides, [roleType]: event.target.value },
                        }))
                      }
                      className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                    >
                      {SIDE_OPTIONS.map(side => (
                        <option key={side} value={side}>
                          {sideLabel(side)} ({side})
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-[11px] text-gray-400">
                      {sideLabel(
                        entity.roles.find(role => role.role_type === roleType)?.side_alignment ?? 'unknown'
                      )}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setDraft(current => pending
                        ? {
                          ...current,
                          addedRoleTypes: current.addedRoleTypes.filter(type => type !== roleType),
                        }
                        : {
                          ...current,
                          removedRoleTypes: removed
                            ? current.removedRoleTypes.filter(type => type !== roleType)
                            : [...current.removedRoleTypes, roleType],
                        })
                    }
                    title={
                      pending ? 'Drop this pending role'
                        : removed ? 'Keep this role'
                        : 'Remove this role'
                    }
                    className="ml-auto text-gray-400 transition-colors hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {addableRoles.length > 0 && (
          <select
            aria-label="Add role"
            value=""
            onChange={event =>
              setDraft(current => ({
                ...current,
                addedRoleTypes: [...current.addedRoleTypes, event.target.value],
              }))
            }
            className="mt-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700"
          >
            <option value="">Add role...</option>
            {addableRoles.map(role => (
              <option key={role} value={role}>
                {roleLabel(role)}
              </option>
            ))}
          </select>
        )}
      </div>

      <div>
        <label
          htmlFor={`aliases-${entity.id}`}
          className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        >
          Aliases (comma separated)
        </label>
        <textarea
          id={`aliases-${entity.id}`}
          value={draft.aliasesText}
          onChange={event => setDraft(current => ({ ...current, aliasesText: event.target.value }))}
          rows={2}
          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700"
        />
      </div>

      <div>
        <label
          htmlFor={`description-${entity.id}`}
          className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-gray-500"
        >
          Description
        </label>
        <textarea
          id={`description-${entity.id}`}
          value={draft.description}
          onChange={event => setDraft(current => ({ ...current, description: event.target.value }))}
          rows={4}
          className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs leading-relaxed text-gray-700"
        />
      </div>

      <div className="flex items-center gap-3">
        <Button
          size="sm"
          icon={Save}
          text="Save"
          loading={saving}
          loadingText="Saving"
          disabled={!isDirty}
          onClick={() => { void handleSave(); }}
        />
        <Button
          size="sm"
          variant="ghost"
          text="Reset"
          disabled={saving || !isDirty}
          onClick={() => setDraft(draftFor(entity))}
        />
        <div className="ml-auto">
          {confirmDelete ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="destructive"
                icon={Trash2}
                text="Confirm delete"
                loading={deleting}
                loadingText="Deleting"
                onClick={() => { void handleDelete(); }}
              />
              <Button
                size="sm"
                variant="ghost"
                text="Cancel"
                disabled={deleting}
                onClick={() => setConfirmDelete(false)}
              />
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              icon={Trash2}
              text="Delete entity"
              tooltip="Deletes the canonical entity; observations are unlinked, not deleted"
              onClick={() => setConfirmDelete(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const EntityAdmin = () => {
  const { slug } = useParams({ from: '/matter/$slug/entity-admin' });
  const matterGuid = slug as string;
  const { isSuperAdmin, isLoading: adminLoading } = useSuperAdminCheck();
  const { entities, isLoading, isError, mutate } = useEntities(isSuperAdmin ? matterGuid : undefined);
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useShellHeader({ header: 'Entity Admin' });

  const filtered = useMemo(() => {
    const lower = query.trim().toLowerCase();
    if (!lower) return entities;
    return entities.filter(entity =>
      entity.name.toLowerCase().includes(lower) ||
      entity.aliases.some(alias => alias.toLowerCase().includes(lower))
    );
  }, [entities, query]);

  if (adminLoading) {
    return <div className="flex items-center justify-center py-20"><Loading /></div>;
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-gray-500">
        Super admin access required.
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col gap-4 px-6 py-6">
      <div>
        <p className="text-xs text-gray-500">
          Manual corrections to consolidated entities. Consolidation can re-derive roles and
          descriptions when new documents are ingested, so edits may be overwritten; a forced side
          is locked against re-inference.
        </p>
      </div>

      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder="Search entities..."
        className="w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm text-gray-700"
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loading /></div>
      ) : isError ? (
        <div className="flex items-center justify-center py-20 text-red-400">Failed to load entities.</div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-white">
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">No entities.</p>
          ) : (
            filtered.map(entity => (
              <div key={entity.id} className="border-b border-gray-100 last:border-b-0">
                <button
                  type="button"
                  onClick={() => setExpandedId(current => (current === entity.id ? null : entity.id))}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-800">{entity.name}</span>
                  <span className="shrink-0 text-[11px] text-gray-400">{entity.entity_type}</span>
                  <span className="shrink-0 text-[11px] text-gray-400">
                    {entity.roles.length} role{entity.roles.length !== 1 ? 's' : ''}
                  </span>
                </button>
                {expandedId === entity.id && (
                  <EntityEditor
                    // Remount on any server-side change so the draft is rebuilt
                    // from fresh data instead of holding pre-save values.
                    key={[
                      entity.canonical_name,
                      entity.description ?? '',
                      entity.stored_aliases.join('|'),
                      entity.roles.map(role => `${role.role_type}:${role.side_alignment}`).join('|'),
                    ].join('~')}
                    entity={entity}
                    matterGuid={matterGuid}
                    onSaved={() => mutate()}
                    onDeleted={async () => {
                      setExpandedId(null);
                      await mutate();
                    }}
                  />
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default EntityAdmin;
